import type { CopyStatus } from "@/lib/clipboard";
import { ActionMenu, ActionMenuItem, ActionMenuNote } from "@/components/ui/ActionMenu";
import { FlagRuleSelect } from "./FlagRuleSelect";
import type { GroupRule } from "@/lib/api/groupRules";

/**
 * The ⋮ menu on a post: copy link, edit, hide/show again, delete, flag — plus the flag
 * reason form, which opens as a sibling popover in the same corner.
 *
 * EVERY ACTION IS A CAPABILITY, NOT A ROLE. The props were `isAuthor` and
 * `isMod`, and the menu decided from them which of three actions to draw:
 * `isMod && !isAuthor` drew Skjul, `isAuthor || isMod` drew Slet. That is an
 * authorisation rule, restated in a component, in a language that cannot
 * enforce it. `PostCapabilities` comes from the predicate the route applies
 * (D11), so the menu now draws what the viewer may actually do.
 */
export function PostActionMenu({
  open,
  setOpen,
  copyStatus,
  onCopyLink,
  canEdit,
  onStartEdit,
  editLoading,
  canHide,
  onHide,
  canUnhide,
  onUnhide,
  canDelete,
  onDelete,
  canFlag,
  flagDone,
  showFlagForm,
  setShowFlagForm,
  flagReason,
  setFlagReason,
  flagRules,
  flagRuleId,
  setFlagRuleId,
  flagLoading,
  onFlag,
  actionError,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  copyStatus: CopyStatus;
  onCopyLink: () => void;
  canEdit: boolean;
  onStartEdit: () => void;
  editLoading: boolean;
  canHide: boolean;
  onHide: () => void;
  /** `hide`'s mirror: true for a moderator on a post that IS hidden (OSO-378). */
  canUnhide: boolean;
  onUnhide: () => void;
  canDelete: boolean;
  onDelete: () => void;
  canFlag: boolean;
  flagDone: boolean;
  showFlagForm: boolean;
  setShowFlagForm: (open: boolean) => void;
  flagReason: string;
  setFlagReason: (reason: string) => void;
  flagRules: GroupRule[];
  flagRuleId: GroupRule["id"] | null;
  setFlagRuleId: (val: GroupRule["id"] | null) => void;
  flagLoading: boolean;
  onFlag: () => void;
  actionError: string | null;
}) {
  return (
    <ActionMenu
      label="Handlinger for dette opslag"
      className="ml-auto"
      open={open}
      onOpenChange={setOpen}
      extra={
        showFlagForm && (
          <div className="absolute right-0 top-8 bg-surface rounded-lg shadow-lg border border-border p-3 z-50 w-64">
            <p className="text-xs font-medium text-foreground mb-2">Hvad er problemet?</p>
            <div className="mb-1.5">
              <FlagRuleSelect rules={flagRules} value={flagRuleId} onChange={setFlagRuleId} />
            </div>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              rows={3}
              placeholder="Valgfri begrundelse..."
              className="w-full rounded border border-border-strong bg-surface text-foreground px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            {actionError && (
              <p className="text-xs text-destructive mt-1">{actionError}</p>
            )}
            <div className="flex gap-2 mt-2">
              <button
                onClick={onFlag}
                disabled={flagLoading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {flagLoading ? "Sender..." : "Send anmeldelse"}
              </button>
              <button
                onClick={() => setShowFlagForm(false)}
                className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Annuller
              </button>
            </div>
          </div>
        )
      }
    >
      {/*
        KEEPS THE MENU OPEN ON PURPOSE. `onCopyLink` closes it itself, but only
        when the copy SUCCEEDED — on failure the row becomes "Kunne ikke
        kopiere", and auto-closing would take that message off the screen
        before it could be read.
      */}
      <ActionMenuItem onSelect={onCopyLink} keepOpen>
        {copyStatus === "copied"
          ? "Kopieret ✓"
          : copyStatus === "failed"
            ? "Kunne ikke kopiere"
            : "Link til opslag"}
      </ActionMenuItem>
      {canEdit && (
        <ActionMenuItem onSelect={onStartEdit} disabled={editLoading}>
          Rediger
        </ActionMenuItem>
      )}
      {canHide && <ActionMenuItem onSelect={onHide}>Skjul</ActionMenuItem>}
      {canUnhide && <ActionMenuItem onSelect={onUnhide}>Vis igen</ActionMenuItem>}
      {canDelete && (
        <ActionMenuItem onSelect={onDelete} destructive>
          Slet
        </ActionMenuItem>
      )}
      {canFlag &&
        (flagDone ? (
          <ActionMenuNote>Anmeldt ✓</ActionMenuNote>
        ) : (
          <ActionMenuItem onSelect={() => setShowFlagForm(true)}>Anmeld</ActionMenuItem>
        ))}
    </ActionMenu>
  );
}
